'use client';

import type { HTMLAttributes, FC, ReactNode } from 'react';
import { useId } from 'react';
import { Button } from '../Button/Button';

export type BypassBlockProps = Pick<HTMLAttributes<HTMLDivElement>, 'className'> & {
  /**
   * The content that should be skipped.
   */
  children: ReactNode;
  /**
   * The text that will be displayed on the button, e.g. "Skip header".
   */
  buttonText: string;
  /**
   * Whether or not you want to skip the scrollTo function.
   */
  skipScrollIntoView?: boolean;
};

/**
 * A component that allows users to skip a block of content.
 */
const BypassBlock: FC<BypassBlockProps> = ({
  children,
  buttonText,
  skipScrollIntoView,
  className,
  ...props
}) => {
  const anchorId = useId();

  return (
    <>
      <div {...props} className={`relative ${className ?? ''}`}>
        <Button
          tabIndex={0}
          variant="default"
          size="base"
          className="focus-visible:border-color-info text-color-info-contrasttext text-size-h5 focus-visible:z-tooltip absolute h-0 w-0 overflow-hidden whitespace-normal rounded border-0 font-bold focus-visible:left-2.5 focus-visible:top-2.5 focus-visible:h-auto focus-visible:w-auto focus-visible:rounded focus-visible:border-2 focus-visible:border-solid focus-visible:p-2.5 focus-visible:shadow-md"
          onClick={() => {
            const element = document.getElementById(anchorId);
            if (element) {
              const rect = element.getBoundingClientRect();
              !skipScrollIntoView &&
                window.scrollTo({
                  left: 0,
                  top: rect.top + window.scrollY,
                  behavior: 'smooth',
                });
              element.focus();
            }
          }}
        >
          {buttonText}
        </Button>
        {children}
      </div>
      <button
        id={anchorId}
        type="button"
        aria-hidden
        className="h-0 w-0 overflow-hidden"
        tabIndex={-1}
      />
    </>
  );
};

export { BypassBlock };
